import React from "react";
import { CustomerBaiscDetailsLayout } from "../../CustomerBasicDetailsLayout/CustomerBasicDetailsLayout";
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { StandadCard } from "../../../../../components/Cards";
import { Box } from "@mui/material";
import { PolicyTabCard } from "../../../../../components/PolicyTabCard/PolicyTabCard";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../redux/initialStoreState";
import { useDispatchWrapper } from "../../../../../hooks";
import { PageLoader } from "../../../../../components/PageLoader/PageLoader";
import { LoadState } from "../../../../../constants/enums";
import { Update } from "@mui/icons-material";
import { clearCustomerPolicyDetailsState } from "../../../../../redux/customerPolicyDetails/customerPolicyDetailsActions";

export const CommAutomobilePolicyLayout: React.FC = () => {
  const { customerId, customerPolicyId } = useParams() as {
    customerId?: number;
    customerPolicyId?: number;
  };
  // The merged customer-policy-details fetch is dispatched by the CustomerInfo
  // screen via useCustomerPolicyDetails. The layout only observes that shared
  // slice for loading/error and the saved customer_policy_id used to enable the
  // sidebar tabs, falling back to the route param.
  const {
    customerPolicyId: mergedCustomerPolicyId,
    details: { loading, error },
  } = useSelector(
    (storeState: IStoreState) => storeState.customerPolicyDetails
  );
  // Prefer the URL id as the source of truth. Only fall back to the merged
  // slice id when the URL has none (e.g. right after a new policy is saved in
  // this session). Preferring the slice id here caused a cross-customer leak
  // when switching between customers.
  const savedCustomerPolicyId = customerPolicyId ?? mergedCustomerPolicyId;
  const navigate = useNavigate();
  const dispatch = useDispatchWrapper();
  const location = useLocation();
  const currentPathname = location.pathname;
  const isMainTab = currentPathname.includes("basic-details");

  const handleStep = (step: string) => () => {
    // const isAlreadyInSamePage = currentPathname.includes(step);
    // if (!isAlreadyInSamePage) {
    navigate(
      `/comm-automobile-policy/${customerId}/${step}/${savedCustomerPolicyId}?tab=0`
    );
    // }
  };

  const renderComponent = () => {
    if (isMainTab) {
      return <Outlet />;
    } else if (!isMainTab && !customerPolicyId) {
      return <Navigate to={`/customer-overview/${customerId}`} />;
    } else if (!isMainTab && savedCustomerPolicyId) {
      return <Outlet context={[customerId, savedCustomerPolicyId]} />;
    } else {
      console.log("__FALIED____");
      return null;
    }
  };

  // Clear the merged customer-policy-details slice whenever the customer or
  // policy in the URL CHANGES. React Router keeps this layout mounted while
  // only the :customerId/:customerPolicyId params change (same route pattern),
  // so the unmount cleanup below never fires when navigating A -> B -> A.
  // Without this reset, the previously opened customer's details stay in the
  // slice and leak into the next customer's form until the re-fetch resolves.
  // The first run is skipped so it doesn't wipe the child form's initial
  // in-flight fetch (effects run child-first, then parent).
  const hasMountedRef = React.useRef(false);
  React.useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    dispatch(clearCustomerPolicyDetailsState());
    dispatch({ type: "CLEAR_POLICY_STATE" });
  }, [customerId, customerPolicyId]);

  React.useEffect(() => {
    return () => {
      dispatch(clearCustomerPolicyDetailsState());
      dispatch({ type: "CLEAR_POLICY_STATE" });
    };
  }, []);

  return (
    <>
      <CustomerBaiscDetailsLayout
        customerId={customerId}
        fromPolicy
        policyId={savedCustomerPolicyId as number}
      >
          <StandadCard sx={{ mb: 1 }}>
            <Box display={"flex"} flexWrap={"wrap"}>
              <PolicyTabCard
                label="Basic Details"
                onClick={handleStep("basic-details")}
                isSelected={currentPathname.includes("basic-details")}
              />
              <PolicyTabCard
                label="Driver"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("driver-tab")}
                isSelected={currentPathname.includes("driver-tab")}
              />
              <PolicyTabCard
                label="Vehicle"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("vehicle-tab")}
                isSelected={currentPathname.includes("vehicle-tab")}
              />
              <PolicyTabCard
                label="Garage Plates"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("garage-plate-tab")}
                isSelected={currentPathname.includes("garage-plate-tab")}
              />
              {/* <PolicyTabCard
                label="Commercial Vehicle Supplement"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("comm-vehicle-supplement-tab")}
                isSelected={currentPathname.includes(
                  "comm-vehicle-supplement-tab"
                )}
              /> */}
              <PolicyTabCard
                label="Coverage"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("vehicle-coverage-schedule-tab")}
                isSelected={currentPathname.includes(
                  "vehicle-coverage-schedule-tab"
                )}
              />
              <PolicyTabCard
                label="Risk Profile"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("risk-profile-tab")}
                isSelected={currentPathname.includes("risk-profile-tab")}
              />
              <PolicyTabCard
                label="Applications"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("application-tab")}
                isSelected={currentPathname.includes("application-tab")}
              />

              <PolicyTabCard
                label="Marketing"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("marketing-tab")}
                isSelected={currentPathname.includes("marketing-tab")}
              />
              <PolicyTabCard
                label="Premium Finance"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("premium-finance")}
                isSelected={currentPathname.includes("premium-finance")}
              />

              {/* <PolicyTabCard
                label="Policy Notes"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("policy-notes")}
                isSelected={currentPathname.includes("policy-notes")}
              /> */}

              <PolicyTabCard
                label="Policy Forms"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("policy-forms-tab")}
                isSelected={currentPathname.includes("policy-forms-tab")}
              />
              <PolicyTabCard
                label="G4 Sign"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("g4-sign")}
                isSelected={currentPathname.includes("g4-sign")}
              />
              <PolicyTabCard
                label="Updates"
                icon={Update}
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("updates")}
                isSelected={currentPathname.includes("updates")}
              />

              <PolicyTabCard
                label="Attachments"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("attachments-tab")}
                isSelected={currentPathname.includes("attachments-tab")}
              />
              <PolicyTabCard
                label="Telematics"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("telematics")}
                isSelected={currentPathname.includes("telematics")}
              />
              <PolicyTabCard
                label="My Proof of Insurance"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("csio-mpoi")}
                isSelected={currentPathname.includes("csio-mpoi")}
              />
              <PolicyTabCard
                label="Policy Changes/Upload"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("policy-change-request-list")}
                isSelected={currentPathname.includes(
                  "policy-change-request-list"
                )}
              />
              <PolicyTabCard
                label="Contacts"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("contacts")}
                isSelected={currentPathname.includes("contacts")}
              />
              <PolicyTabCard
                label="eDocs"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("csio-edocs-list")}
                isSelected={currentPathname.includes("csio-edocs-list")}
              />
              <PolicyTabCard
                label="Activities"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("all-tasks")}
                isSelected={currentPathname.includes("all-tasks")}
              />
              <PolicyTabCard
                label="Email Thread"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("email-thread")}
                isSelected={currentPathname.includes("email-thread")}
              />
              <PolicyTabCard
                label="Sequences"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("sequences")}
                isSelected={currentPathname.includes("sequences")}
              />
              <PolicyTabCard
                label="Invoices"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("invoices")}
                isSelected={currentPathname.includes("invoices")}
              />
                <PolicyTabCard
                label="Certificate of Insurance"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("certificate-of-insurance")}
                isSelected={currentPathname.includes("certificate-of-insurance")}
              />
              <PolicyTabCard
                label="Driver Profile"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("driver-profile-tab")}
                isSelected={currentPathname.includes("driver-profile-tab")}
              />
              {/* <PolicyTabCard
                label="Abeyance/Notes"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("notes-tab")}
                isSelected={currentPathname.includes("notes-tab")}
              /> */}

              {/* <PolicyTabCard
                label="Invoice Payment"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("invoices/payments")}
                isSelected={currentPathname.includes("invoices/payments")}
              /> */}
            </Box>
          </StandadCard>
          <PageLoader
            loading={loading === LoadState.InProgress}
            error={error ? { message: error } : null}
          >
            {renderComponent()}
          </PageLoader>
        </CustomerBaiscDetailsLayout>
    </>
  );
};
